import apiClient, { USE_MOCK } from '@shared/api/client';
import { ENDPOINTS } from '@shared/api/endpoints';
import { mockLampData } from './mockLampData';
import { getStoredLanguage } from '@shared/i18n';

/**
 * 點燈活動 API Service
 * 根據 USE_MOCK 設定決定使用真實 API 或 Mock 資料
 */

/**
 * 模擬網路延遲
 */
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 取得傳燈活動 Banner（單張）
 * POST /v1/member/banner/listByEvent
 */
export const getWisdomLampBanner = async () => {
  const res = await apiClient.post(ENDPOINTS.BANNER.LIST_BY_EVENT, { event_id: 1 }, { skipLang: true });
  const isEn = getStoredLanguage() === 'en';
  if (isEn && res.media) {
    res.media = res.media.map((item) => ({
      ...item,
      details: item.details ? {
        ...item.details,
        title: item.details.title_en || item.details.title,
        subtitle: item.details.subtitle_en || item.details.subtitle,
        content: item.details.content_en || item.details.content,
      } : item.details,
    }));
  }
  return res;
};

/**
 * 取得智慧之燈頁面資料
 */
export const getWisdomLampData = async () => {
  if (USE_MOCK) {
    await delay(300);
    return {
      success: true,
      data: mockLampData.wisdomLamp,
    };
  }

  return apiClient.get(ENDPOINTS.LAMP.WISDOM);
};

/**
 * 取得傳燈時程列表
 * POST /v1/lighting/period/list
 * @param {Object} params - 查詢參數（選填）
 * @param {boolean} [params.show_video_only] - 只回傳有路線圖影片的週次
 */
export const getLampSchedule = async (params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    return mockLampData.schedule;
  }

  return apiClient.post(ENDPOINTS.LAMP.PERIOD_LIST, params);
};

/**
 * 取得傳燈區域精舍列表
 * POST /v1/lighting/region/list
 */
export const getLampRegionList = async () => {
  if (USE_MOCK) {
    await delay(300);
    return mockLampData.regionList;
  }

  return apiClient.post(ENDPOINTS.LAMP.REGION_LIST, {});
};

/**
 * 取得點燈佈告欄資料
 */
export const getLampBulletin = async () => {
  if (USE_MOCK) {
    await delay(400);
    return {
      success: true,
      data: mockLampData.bulletin,
    };
  }

  return apiClient.get(ENDPOINTS.LAMP.BULLETIN);
};

/**
 * 提交點燈表單
 * @param {Object} formData - 表單資料
 */
export const submitLampLighting = async (formData) => {
  if (USE_MOCK) {
    await delay(800);
    // 模擬成功提交
    return {
      success: true,
      data: {
        id: `blessing_${Date.now()}`,
        message: '點燈祈福提交成功',
        ...formData,
      },
    };
  }

  return apiClient.post(ENDPOINTS.LAMP.SUBMIT, formData);
};

/**
 * 取得祈福結果
 * @param {string} id - 祈福 ID
 */
export const getBlessingResult = async (id) => {
  if (USE_MOCK) {
    await delay(400);
    return {
      success: true,
      data: mockLampData.blessingResult,
    };
  }

  return apiClient.get(ENDPOINTS.LAMP.BLESSING_RESULT(id));
};

/**
 * 取得文星點燈花絮列表
 * POST /v1/event/behindScenes/list
 * event_id = 1 (文星點燈專屬)
 */
export const getBehindScenesList = async (params = {}) => {
  return apiClient.post(ENDPOINTS.BEHIND_SCENES.LIST, { event_id: 1, ...params });
};

/**
 * 取得祈願類別列表
 * GET /v1/vow/category/list
 */
export const getVowCategoryList = async () => {
  if (USE_MOCK) {
    await delay(300);
    return mockLampData.vowCategoryList;
  }

  return apiClient.get(ENDPOINTS.LAMP.VOW_CATEGORY_LIST);
};

/**
 * 新增祈願記錄（點燈祈福送出）
 * POST /v1/vow/record/add
 * 需要 Member Token（已登入）
 * @param {Object} params
 * @param {number} params.category_id - 祈願類別 ID
 * @param {string} params.vower_name - 功德主姓名
 * @param {string} params.monastery_name - 精舍名稱
 * @returns {Promise<{content, message, monastery_name, result_code, serial_num, title, vower_name}>}
 */
export const addVowRecord = async (params) => {
  if (USE_MOCK) {
    await delay(800);
    return {
      content: '中國有一句名言:「同船過渡都是百世修」，能成為夫妻就是一種緣，所以彼此之間要互相尊重、互相關心、互相照顧，從生到死都應該如此，因為這種福報、這種善緣是不容易的。<br><br>－－惟覺安公老和尚',
      message: 'Success',
      monastery_name: params.monastery_name,
      result_code: 200,
      serial_num: '2-1',
      title: '家庭和樂',
      vower_name: params.vower_name,
    };
  }

  return apiClient.post(ENDPOINTS.LAMP.VOW_RECORD_ADD, params);
};

/**
 * 取得祈願記錄列表
 * POST /v1/vow/record/list
 * @param {Object} params - 查詢參數（皆為選填，預設全部顯示）
 * @param {string} [params.vower_name] - 功德主姓名（模糊搜尋）
 * @param {string} [params.date] - 日期篩選 YYYY-MM-DD
 * @param {boolean} [params.only_self] - 只顯示自己的記錄
 */
export const getVowRecordList = async (params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    return mockLampData.vowRecordList;
  }

  return apiClient.post(ENDPOINTS.LAMP.VOW_RECORD_LIST, params);
};

/**
 * 取得祈願記錄詳細描述（點擊燈籠時使用）
 * POST /v1/vow/record/describe
 * @param {number} recordId - 祈願記錄 ID
 * @returns {Promise<{content, message, monastery_name, result_code, title, vower_name}>}
 */
export const getVowRecordDescribe = async (recordId) => {
  if (USE_MOCK) {
    await delay(400);
    // 模擬回傳與 addVowRecord 相同格式的資料
    return {
      content: '中國有一句名言:「同船過渡都是百世修」，能成為夫妻就是一種緣，所以彼此之間要互相尊重、互相關心、互相照顧，從生到死都應該如此，因為這種福報、這種善緣是不容易的。<br><br>－－惟覺安公老和尚',
      message: 'Success',
      monastery_name: '普天精舍',
      result_code: 200,
      title: '家庭和樂',
      vower_name: '王大明',
    };
  }

  return apiClient.post(ENDPOINTS.LAMP.VOW_RECORD_DESCRIBE, { record_id: recordId });
};

/**
 * 精舍報導按讚 / 取消讚
 * POST /v1/lighting/post/like
 * @param {number} postId - 報導 ID
 * @param {boolean} like - true = 按讚, false = 取消讚
 * @returns {Promise<{like_count, message, post_id, result_code}>}
 */
export const toggleLightingPostLike = async (postId, like) => {
  if (USE_MOCK) {
    await delay(300);
    return {
      like_count: like ? 1 : 0,
      message: 'Success',
      post_id: postId,
      result_code: 200,
    };
  }

  return apiClient.post(ENDPOINTS.LAMP.LIGHTING_POST_LIKE, {
    post_id: postId,
    like,
  });
};

/**
 * 取得供燈影音報導列表（不含詳細內容）
 * POST /v1/lighting/post/list
 */
export const getLightingPostSummaryList = async () => {
  return apiClient.post(ENDPOINTS.LAMP.LIGHTING_POST_LIST, { show_detail: false });
};

/**
 * 取得傳燈報導列表
 * POST /v1/lighting/post/list
 * @param {Object} params - 查詢參數
 * @param {number} [params.monastery_id] - 精舍 ID
 * @param {number} [params.post_id] - 報導 ID
 * @param {number} [params.page] - 頁碼（預設 1）
 * @param {number} [params.per_page] - 每頁筆數（預設 10）
 * @param {boolean} [params.verified] - 是否已驗證
 * @param {boolean} [params.published] - 是否已發布
 * @returns {Promise<{current_page, items, total, total_items, total_pages}>}
 */
export const getLightingPostList = async (params = {}) => {
  if (USE_MOCK) {
    await delay(400);
    // 從 mock 資料中根據 monastery_id 篩選
    const allPosts = mockLampData.lightingPostList?.items || [];
    const filteredPosts = params.monastery_id
      ? allPosts.filter(post => post.monastery_id === params.monastery_id)
      : allPosts;

    return {
      current_page: params.page || 1,
      items: filteredPosts.slice(0, params.per_page || 10),
      total: filteredPosts.length,
      total_items: filteredPosts.length,
      total_pages: Math.ceil(filteredPosts.length / (params.per_page || 10)),
      message: 'Success',
      result_code: 200,
    };
  }

  return apiClient.post(ENDPOINTS.LAMP.LIGHTING_POST_LIST, params);
};

/**
 * 取得點燈列表
 * @param {Object} params - 查詢參數 { page, limit, category }
 */
export const getLampList = async (params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    return {
      success: true,
      data: {
        items: mockLampData.list,
        total: mockLampData.list.length,
        page: params.page || 1,
        limit: params.limit || 10,
      },
    };
  }

  return apiClient.get(ENDPOINTS.LAMP.LIST, { params });
};

export default {
  getWisdomLampData,
  getLampSchedule,
  getLampRegionList,
  getLampBulletin,
  submitLampLighting,
  getBlessingResult,
  getLampList,
  getVowCategoryList,
  getVowRecordList,
  addVowRecord,
  getVowRecordDescribe,
  getLightingPostList,
  toggleLightingPostLike,
};
